import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, Check, Sparkles, Quote,
  Briefcase, Rocket, ShieldCheck, Target, Award
} from "lucide-react";
import { STATS, GROWTH_STATS, SERVICES, INDUSTRIES, TESTIMONIALS, QUOTES, CLIENT_LOGOS, COMPANY } from "../data";
import { FeaturedRoles } from "../components/FeaturedRoles";

const ease = [0.16, 1, 0.3, 1];

const employerPoints = [
  "Curated candidate network across 6 industries",
  "Deep bench across IT & Non-IT verticals",
  "72-hour curated shortlists",
  "Dedicated practice leads per industry",
  "No cost until the candidate is onboarded",
];

const candidatePoints = [
  "Active roles across America",
  "Get interviewed within two weeks",
  "Personalized career mapping",
  "Direct access to hiring managers",
  "Confidential, human-led guidance",
];

const industryIcons = {
  it: <Rocket className="w-6 h-6" />,
  healthcare: <ShieldCheck className="w-6 h-6" />,
  defense: <Target className="w-6 h-6" />,
  aerospace: <Award className="w-6 h-6" />,
  "life-sciences": <Sparkles className="w-6 h-6" />,
  manufacturing: <Briefcase className="w-6 h-6" />,
};

export function Home() {
  return (
    <div data-testid="home-page" className="page-enter">
      <HeroSection />
      <DualPathSection />
      <IndustriesSection />
      <GrowthStatsSection />
      <QuoteSection />
      <FeaturedRoles />
      <ServicesSection />
      <ClientLogosSection />
      <TestimonialsSection />
      <FinalCTASection />
      <CompliancePanel />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function CompliancePanel() {
  return (
    <section className="py-14 container-x" data-testid="compliance-panel">
      <div className="card-soft rounded-2xl p-6 sm:p-8 bg-white grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
        <div className="md:col-span-2">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E9610E] font-semibold mb-2">
            Compliance &amp; Legal
          </p>
          <h3 className="font-display font-bold text-xl sm:text-2xl text-[#0F1E3C] tracking-tight">
            Transparent communications, on your terms.
          </h3>
          <p className="text-sm text-[#475569] mt-2 leading-relaxed">
            Albany Staffing is a DBA of Albany Systems LLC. We comply with A2P 10DLC SMS
            requirements. Review our policies anytime — opt out by replying <strong>STOP</strong>,
            get help with <strong>HELP</strong>.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row md:flex-col gap-3 md:justify-self-end">
          <Link
            to="/privacy"
            data-testid="home-privacy-link"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-[#EAE3D2] text-sm font-semibold text-[#0F1E3C] hover:bg-[#FAF7F2] hover:border-[#E9610E] transition-colors"
          >
            Privacy Policy <ArrowUpRight className="w-4 h-4" />
          </Link>
          <Link
            to="/sms-terms"
            data-testid="home-sms-terms-link"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-[#EAE3D2] text-sm font-semibold text-[#0F1E3C] hover:bg-[#FAF7F2] hover:border-[#E9610E] transition-colors"
          >
            SMS Terms &amp; Conditions <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section className="relative pt-36 sm:pt-44 pb-20 sm:pb-28 overflow-hidden paper-texture">
      <div className="blob-amber -top-20 -right-40" />
      <div className="blob-navy -bottom-20 -left-40" />

      <div className="container-x relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#EAE3D2] mb-8 shadow-sm"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#E9610E]" />
          <span className="text-xs font-semibold tracking-wide text-[#0F1E3C]">
            {COMPANY.positioning}
          </span>
        </motion.div>

        <motion.h1
          data-testid="hero-headline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.1 }}
          className="font-display font-extrabold text-[clamp(2.5rem,7.5vw,6rem)] leading-[1.02] tracking-tight text-balance max-w-5xl text-[#0F1E3C]"
        >
          Your trusted partner <br />
          for <span className="gradient-text-amber italic font-serif">hiring that lasts.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.25 }}
          className="mt-8 text-lg sm:text-xl text-[#475569] max-w-2xl leading-relaxed"
        >
          Albany Staffing is a premier US staffing &amp; recruitment firm placing senior
          engineers, technologists, and operators across IT, Healthcare, Defense,
          Aerospace, Life Sciences, and Manufacturing.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.4 }}
          className="mt-10 flex flex-col sm:flex-row gap-3"
        >
          <Link to="/contact" data-testid="hero-cta-start" className="btn-accent">
            Request Talent <ArrowUpRight className="w-4 h-4" />
          </Link>
          <Link to="/careers" data-testid="hero-cta-apply" className="btn-outline">
            Find Your Next Role <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-10 max-w-4xl"
        >
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 + i * 0.1, ease }}
              data-testid={`stat-${i}`}
              className="border-l-2 border-[#E9610E] pl-4"
            >
              <div className="font-display font-bold text-3xl sm:text-4xl text-[#0F1E3C] leading-none">
                {s.value}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.12em] text-[#64748B] font-semibold">
                {s.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
function DualPathSection() {
  return (
    <section className="section-y container-x">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        <PathCard
          eyebrow="For Employers"
          title="Trusted partner for your hiring needs"
          description="From director-level direct hires to contract surges — we shortlist in 72 hours, close in weeks, and retain for years."
          points={employerPoints}
          cta="Request Talent"
          ctaLink="/contact"
          testId="path-employer"
          variant="navy"
        />
        <PathCard
          eyebrow="For Candidates"
          title="Looking for a great career? Start here."
          description="We place senior engineers and operators at America's most respected companies — with dignity, transparency, and speed."
          points={candidatePoints}
          cta="Find Jobs"
          ctaLink="/careers"
          testId="path-candidate"
          variant="white"
        />
      </div>
    </section>
  );
}

function PathCard({ eyebrow, title, description, points, cta, ctaLink, testId, variant }) {
  const isNavy = variant === "navy";
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease }}
      data-testid={testId}
      className={`relative rounded-3xl p-8 sm:p-12 overflow-hidden ${
        isNavy
          ? "bg-[#0F1E3C] text-white"
          : "bg-white border border-[#EAE3D2] text-[#0F1E3C]"
      }`}
    >
      {isNavy && <div className="blob-amber -bottom-20 -right-20 opacity-50" />}
      <div className="relative z-10">
        <p className={`text-xs uppercase tracking-[0.2em] font-semibold mb-4 ${isNavy ? "text-[#E9610E]" : "text-[#E9610E]"}`}>
          {eyebrow}
        </p>
        <h2 className={`font-display font-bold text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.05] tracking-tight mb-5 text-balance ${isNavy ? "text-white" : "text-[#0F1E3C]"}`}>
          {title}
        </h2>
        <p className={`text-base leading-relaxed mb-7 ${isNavy ? "text-white/70" : "text-[#475569]"}`}>
          {description}
        </p>

        <ul className="space-y-3 mb-8">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-3">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isNavy ? "bg-[#E9610E]" : "bg-[#E9610E]"}`}>
                <Check className="w-3 h-3 text-white" />
              </span>
              <span className={`text-sm ${isNavy ? "text-white/85" : "text-[#334155]"}`}>{p}</span>
            </li>
          ))}
        </ul>

        <Link
          to={ctaLink}
          className={`inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm transition-all duration-300 ${
            isNavy
              ? "bg-[#E9610E] text-white hover:bg-[#D55100]"
              : "bg-[#0F1E3C] text-white hover:bg-[#1a2e5c]"
          } hover:scale-[1.02]`}
        >
          {cta} <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function IndustriesSection() {
  return (
    <section className="section-y container-x">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-3">Industries We Serve</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#0F1E3C] text-balance max-w-2xl">
            Six verticals. One standard of talent.
          </h2>
        </div>
        <Link
          to="/industries"
          data-testid="home-industries-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#E9610E] hover:gap-3 transition-all"
        >
          Explore all industries <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {INDUSTRIES.map(renderIndustryCard)}
      </div>
    </section>
  );
}

function renderIndustryCard(ind, i) {
  return (
    <motion.div
      key={ind.id}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: i * 0.06, ease }}
      data-testid={"industry-card-" + ind.id}
    >
      <Link
        to="/industries"
        className="block card-soft rounded-2xl p-6 sm:p-7 h-full transition-all duration-300 group"
      >
        <div className="w-12 h-12 rounded-xl bg-[#FFF4EA] flex items-center justify-center text-[#E9610E] mb-5 group-hover:bg-[#E9610E] group-hover:text-white transition-colors">
          {industryIcons[ind.id]}
        </div>
        <h3 className="font-display font-bold text-xl text-[#0F1E3C] mb-2">
          {ind.name}
        </h3>
        <p className="text-sm text-[#475569] leading-relaxed mb-4">
          {ind.blurb}
        </p>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#E9610E] group-hover:gap-2 transition-all">
          Learn more <ArrowUpRight className="w-3.5 h-3.5" />
        </span>
      </Link>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function GrowthStatsSection() {
  return (
    <section className="relative py-20 sm:py-24 overflow-hidden">
      <div className="absolute inset-0 bg-[#0F1E3C]" />
      <div className="blob-amber top-0 right-0 opacity-40" />

      <div className="container-x relative z-10">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-3 text-center"
        >
          Be a part of our growth story
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display font-bold text-4xl sm:text-5xl text-white tracking-tight text-center max-w-3xl mx-auto mb-14 text-balance"
        >
          Numbers that reflect our commitment.
        </motion.h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10">
          {GROWTH_STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 + i * 0.1, ease }}
              data-testid={`growth-stat-${i}`}
              className="text-center"
            >
              <div className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-none">
                {s.value}
              </div>
              <div className="mt-3 text-xs sm:text-sm uppercase tracking-[0.12em] text-white/60 font-semibold">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
function QuoteSection() {
  const q = QUOTES[0];
  return (
    <section className="section-y container-x">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease }}
        className="relative max-w-5xl mx-auto text-center"
      >
        <Quote className="w-12 h-12 text-[#E9610E] mx-auto mb-6 opacity-70" strokeWidth={1.5} />
        <blockquote
          data-testid="hero-quote"
          className="font-serif italic text-3xl sm:text-5xl lg:text-6xl text-[#0F1E3C] leading-[1.15] tracking-tight text-balance"
        >
          &ldquo;{q.text}&rdquo;
        </blockquote>
        {q.source && (
          <p className="mt-8 text-sm uppercase tracking-[0.2em] text-[#64748B] font-semibold">
            — {q.source}
          </p>
        )}
      </motion.div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
function ServicesSection() {
  return (
    <section className="section-y bg-white border-y border-[#EAE3D2]">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-3">What We Do</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#0F1E3C] text-balance max-w-2xl">
              Full-spectrum staffing. Direct hire at the core.
            </h2>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#E9610E] hover:gap-3 transition-all"
          >
            View all services <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map(renderServiceTile)}
        </div>
      </div>
    </section>
  );
}

function renderServiceTile(s, i) {
  return (
    <motion.div
      key={s.id}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: i * 0.05, ease }}
      data-testid={"service-card-" + s.id}
    >
      <Link
        to="/services"
        className="block card-soft rounded-2xl p-7 h-full group transition-all duration-300"
      >
        <div className="flex items-start justify-between mb-5">
          <span className="text-xs font-semibold text-[#E9610E] tracking-wider">{s.number}</span>
          <ArrowUpRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#E9610E] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
        </div>
        <h3 className="font-display font-bold text-xl text-[#0F1E3C] mb-3">{s.title}</h3>
        <p className="text-sm text-[#475569] leading-relaxed">{s.description}</p>
      </Link>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function ClientLogosSection() {
  return (
    <section className="py-16 bg-[#FAF7F2] overflow-hidden">
      <div className="container-x mb-10">
        <p className="text-xs uppercase tracking-[0.25em] text-[#64748B] font-semibold text-center">
          Trusted by teams that ship real products
        </p>
      </div>
      <div className="marquee-mask">
        <div className="flex animate-marquee whitespace-nowrap">
          {renderLogoRow("a")}
          {renderLogoRow("b")}
        </div>
      </div>
    </section>
  );
}

function renderLogoRow(keyPrefix) {
  return (
    <div className="flex items-center gap-12 sm:gap-16 px-6">
      {CLIENT_LOGOS.map((name) => (
        <div
          key={keyPrefix + "-" + name}
          className="font-display font-bold text-2xl sm:text-3xl text-[#94A3B8] hover:text-[#0F1E3C] transition-colors whitespace-nowrap"
        >
          {name}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function TestimonialsSection() {
  return (
    <section className="section-y container-x">
      <div className="mb-14">
        <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-3">What Clients Say</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#0F1E3C] text-balance max-w-3xl">
          Proof in the partnerships, not the pitch.
        </h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {TESTIMONIALS.map(renderTestimonial)}
      </div>
    </section>
  );
}

function renderTestimonial(t, i) {
  return (
    <motion.figure
      key={t.name}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: i * 0.1, ease }}
      data-testid={"testimonial-" + i}
      className="card-soft rounded-2xl p-7 transition-all duration-300"
    >
      <Quote className="w-7 h-7 text-[#E9610E] mb-5 opacity-70" strokeWidth={1.5} />
      <blockquote className="text-base leading-relaxed text-[#0F1E3C] mb-6">
        &ldquo;{t.quote}&rdquo;
      </blockquote>
      <figcaption className="border-t border-[#EAE3D2] pt-5">
        <div className="font-semibold text-[#0F1E3C]">{t.name}</div>
        <div className="text-sm text-[#64748B] mt-1">{t.title}</div>
      </figcaption>
    </motion.figure>
  );
}

// ─────────────────────────────────────────────────────────────────────
function FinalCTASection() {
  return (
    <section className="section-y container-x">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease }}
        className="relative rounded-3xl p-10 sm:p-16 overflow-hidden bg-[#0F1E3C] text-white"
      >
        <div className="blob-amber top-0 right-0 opacity-60" />
        <div className="blob-amber bottom-0 left-0 opacity-40" />

        <div className="relative z-10 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-4">
            Ready when you are
          </p>
          <h3 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6 text-balance">
            Hire the people who will <span className="italic font-serif text-[#E9610E]">define your decade.</span>
          </h3>
          <p className="text-lg text-white/70 mb-8 max-w-2xl">
            Share your role, your timeline, and what&rsquo;s not working with your current
            pipeline. We&rsquo;ll come back within 24 hours with a plan.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact" className="btn-accent" data-testid="final-cta-contact">
              Request Talent <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/careers" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/10 text-white text-sm font-semibold hover:bg-white/20 transition-colors">
              Browse Open Roles
            </Link>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
